import * as React from 'react';
import { useFormikContext } from 'formik';
import { Checkbox } from '../../../../widgets/FormikInputFieldV2';
import { OnboardingModal } from '../../OnboardingModal';
import { StrictSetFieldValue } from '../../../../types';

/**
 * Builds a checkbox, which upon checked would fire a confirmation modal.
 * Used to warn users before they decide not to configure static IP addresses.
 * Must be used within a formik context, with a field "isIPAutoConfig".
 */
export const useIPAutoConfig = () => {
  const {
    setFieldValue,
    isSubmitting,
  }: {
    setFieldValue: StrictSetFieldValue<{ isIPAutoConfig: boolean }>;
    isSubmitting: boolean;
  } = useFormikContext();

  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);

  const onCheckBoxClicked = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      if (e.currentTarget.checked) {
        setConfirmModalOpen(true);
      }
    },
    [setConfirmModalOpen],
  );

  const onCheckBoxChanged = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      setFieldValue('isIPAutoConfig', false);
    },
    [setFieldValue],
  );

  const onCancel = React.useCallback(() => {
    setFieldValue('isIPAutoConfig', false);
    setConfirmModalOpen(false);
  }, [setConfirmModalOpen, setFieldValue]);

  const onOk = React.useCallback(() => {
    setConfirmModalOpen(false);
    setFieldValue('isIPAutoConfig', true);
  }, [setConfirmModalOpen, setFieldValue]);

  const ipAutoConfigCheckbox = (
    <Checkbox
      name="isIPAutoConfig"
      label="I can’t provide a static IP address right now"
      className="ml-4 mt-n1 mb-2"
      onClick={onCheckBoxClicked}
      onChange={onCheckBoxChanged}
      disabled={isSubmitting}
    />
  );

  const ipAutoConfigConfirmModal = (
    <OnboardingModal
      title="DHCP Override"
      visible={confirmModalOpen}
      onCancel={onCancel}
      onOk={onOk}
      okTitle="Enable DHCP Override"
    >
      <div style={{ width: '500px' }}>
        <p>
          If you are unable to provide a static IP address at this time,
          enabling DHCP override will configure the sensor or scanner to use
          DHCP. Additional steps can be taken to set a static IP address before
          installation from the device’s console session.
        </p>
      </div>
    </OnboardingModal>
  );

  return {
    ipAutoConfigCheckbox,
    ipAutoConfigConfirmModal,
  };
};
